import { formatDistance, formatRelative, haversineMetres, type Device, type LatLon } from '@findmy/shared';
import { useAddress } from '../api/queries';
import { BatteryIndicator, DeviceBadges, DeviceTypeIcon } from './DeviceBits';
import { markerState, markerStateClasses } from '../lib/device';

export function DeviceListItem({
  device,
  now,
  me,
  onSelect,
}: {
  device: Device;
  now: number;
  me: LatLon | null;
  onSelect: (id: string) => void;
}) {
  const location = device.lastLocation;
  const { data: address } = useAddress(location);
  const state = markerState(device, now);
  const distance = location && me ? formatDistance(haversineMetres(me, location)) : null;

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(device.id)}
        data-testid={`device-item-${device.id}`}
        className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-blue-500 dark:hover:bg-slate-800"
      >
        <span
          className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white ${markerStateClasses[state]}`}
          aria-hidden
        >
          <DeviceTypeIcon type={device.type} className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate font-medium">{device.name}</span>
            {distance && <span className="shrink-0 text-xs text-slate-500">{distance}</span>}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
            <span className={state === 'very_stale' ? 'text-red-700 dark:text-red-400' : ''}>
              {device.lastSeenAt ? `Seen ${formatRelative(device.lastSeenAt, now)}` : 'Never seen'}
            </span>
            <BatteryIndicator pct={device.batteryPct} charging={device.isCharging} />
          </span>
          <span className="mt-0.5 block truncate text-xs text-slate-500">
            {!location
              ? 'No location yet'
              : address?.address
                ? `Near ${address.address}`
                : address?.status === 'disabled'
                  ? `${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}`
                  : ' '}
          </span>
          <span className="mt-1 flex flex-wrap gap-1 empty:hidden">
            <DeviceBadges device={device} />
          </span>
        </span>
      </button>
    </li>
  );
}
